import { DataSource } from 'hooks/useHttpClient';
import { FactAudit, PaginatedList, PaginationMeta, PaginationMetaResolver } from './Common';
import { FastMemberListFromJobResolver, FastMemberOption, Member, MemberListFromJobResolver } from './Members';
import { BasicMappedStatus, JobStages } from './Candidate';
import { Company, QuickCompanySingleResolver } from './Company';
import { Comment, CommentListResolver } from './Comment';

export const PrivilegedRoles: BasicMappedStatus[] = [
    { id: 2, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Recruiter` },
    { id: 3, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Executive` },
    { id: 4, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Technical` },
    { id: 5, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Hiring Manager` },
    { id: 12, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Recruiter Lead` }
];

export const CompanyRoles: BasicMappedStatus[] = [
    { id: 6, name: 'Company Hiring Manager' },
    { id: 7, name: 'Company Executive' },
    { id: 8, name: 'Company Tech Evaluator' },
    { id: 9, name: 'Company Stakeholder' }
];

export const JobStatuses: (BasicMappedStatus & {
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
})[] = [
    { id: 1, name: 'Active', color: 'primary' },
    { id: 2, name: 'On hold', color: 'warning' },
    { id: 3, name: 'Completed', color: 'success' },
    { id: 4, name: 'Canceled', color: 'error' }
];

export const getJobRole = (role?: string, defaultVal?: string, preferDefault: boolean = false): BasicMappedStatus | undefined => {
    if (preferDefault && defaultVal) {
        return { id: 0, name: defaultVal };
    }

    const foundPrivRole = PrivilegedRoles.find((r) => r.id === +(role || 0));
    if (foundPrivRole) {
        return foundPrivRole;
    }
    const foundCompanyRole = CompanyRoles.find((r) => r.id === +(role || 0));
    if (foundCompanyRole) {
        return foundCompanyRole;
    }
    return defaultVal ? { id: 0, name: defaultVal } : undefined;
};

export type Job = {
    id: number;
    jobTag: string;
    title: string;
    description: string;
    mustHave: string;
    departmentArea: string;
    hardwareRequirements: string;
    softwareRequirements: string | null;
    testingRequirements: string;
    minimumTestResults: number;
    headcount: number;
    headcountCompleted: number;
    percentageCompleted: number;
    status: BasicMappedStatus;
    published: boolean;
    publishedDate: string;
    updatedAudit?: FactAudit;
    createdAudit?: FactAudit;
    approvedAudit?: FactAudit;
    estimatedStartDate: string;
    priority: 'High' | 'Medium' | 'Low';
    minBudget: number;
    maxBudget: number;
    minSalary: number;
    maxSalary: number;
    members: FastMemberOption[];
    fullMembers: Member[];
    hiringManager?: Member; // Would need to be updated. Check with Lonnie
    recruiter?: Member;
    jobApplications: JobPipelineRecord[];
    company?: Company;
    comments: Comment[];
};

export type JobPipelineRecord = {
    applicationId: number;
    email: string;
    fullName: string;
    matchPercentage?: number;
    jobStage: BasicMappedStatus;
};

export function NastyPipelineResolverToApplications(dataSource: DataSource, jobCandidates: any): JobPipelineRecord[] {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                const pipelineRecords: JobPipelineRecord[] = [];
                const candidatePipelineRecords = jobCandidates;
                Object.keys(candidatePipelineRecords).forEach((key) => {
                    const mappedStage = JobStages.find((stage) => stage.name === key);
                    const candidatePipelineRecord = candidatePipelineRecords[key];
                    const candidateApplications: JobPipelineRecord[] = candidatePipelineRecord.map((application: any) => ({
                        applicationId: application.id,
                        email: application.email,
                        fullName: `${application.first_name} ${application.last_name}`,
                        matchPercentage: application.match_percentage || undefined,
                        jobStage: mappedStage || { id: 0, name: 'Unknown' }
                    }));
                    pipelineRecords.push(...candidateApplications);
                });
                return pipelineRecords;
            }

            default:
                return [];
        }
    } catch (error) {
        console.log('NastyPipelineResolverToApplications', error);
        return [];
    }
}

export function JobSingleResolver(dataSource: DataSource, job: any, withApplications: Boolean): Job | null {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                const company = QuickCompanySingleResolver(dataSource, job.company) || undefined;
                const members = FastMemberListFromJobResolver(dataSource, job.members || [], company);
                const fullMembers = MemberListFromJobResolver(dataSource, job.members || []);
                return {
                    id: job.id,
                    jobTag: job.job_requisition_key,
                    title: job.title,
                    description: job.description,
                    mustHave: job.must_have,
                    departmentArea: job.department_area,
                    hardwareRequirements: job.hardware_required,
                    softwareRequirements: job.software_required,
                    testingRequirements: job.test_requirements,
                    minimumTestResults: job.minimum_test_results || 0,
                    headcount: job.headcount,
                    headcountCompleted: -1, // TODO CALCULATE THIS
                    percentageCompleted: -1, // TODO CALCULATE THIS
                    status: JobStatuses.find((status) => status.name === job.status) || JobStatuses[0],
                    published: job.publish === 1,
                    publishedDate: job.publish_date,
                    createdAudit: {
                        userId: job.created_by?.id || 0,
                        userFullName: job.created_by?.name,
                        avatar: job.created_by?.profile?.profile_picture || undefined,
                        dateStr: job.created_at
                    },
                    approvedAudit: job.approved_by
                        ? {
                              userId: job.approved_by?.id || 0,
                              userFullName: job.approved_by?.name,
                              avatar: job.approved_by?.profile?.profile_picture || undefined,
                              dateStr: ''
                          }
                        : undefined,
                    estimatedStartDate: job.estimated_start_date,
                    priority: job.priority,
                    minBudget: +job.min_budget,
                    maxBudget: +job.max_budget,
                    minSalary: +job.min_salary_range,
                    maxSalary: +job.max_salary_range,
                    members,
                    fullMembers,
                    hiringManager: fullMembers.find((member) => member.role?.id === 6),
                    recruiter: fullMembers.find((member) => member.role?.id === 12), // TODO Add more roles
                    jobApplications: withApplications ? NastyPipelineResolverToApplications(dataSource, job.candidates) : [],
                    company,
                    comments: job.comments ? CommentListResolver(dataSource, job.comments, true) : []
                };
            }

            default:
                return null;
        }
    } catch (error) {
        console.log('Super test here', error);
        return null;
    }
}

export function JobListResolver(dataSource: DataSource, apiResponse: any): Job[] {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.map((job: any) => JobSingleResolver(dataSource, job, true)).filter((job: Job | null) => !!job);

            default:
                return [];
        }
    } catch (error) {
        console.log('JobListResolver', error);
        return [];
    }
}

export function JobPaginatedListResolver(dataSource: DataSource, apiResponse: any): PaginatedList<Job> | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    data: apiResponse.data
                        .map((job: any) => JobSingleResolver(dataSource, job, true))
                        .filter((job: Job | null) => !!job) as Job[],
                    meta: PaginationMetaResolver(dataSource, apiResponse.meta) as PaginationMeta
                };

            default:
                return null;
        }
    } catch (error) {
        console.log('JobPaginatedListResolver', error);
        return null;
    }
}

export function JobAsOptionsList(dataSource: DataSource, apiResponse: any): BasicMappedStatus[] {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                if (apiResponse.data) {
                    apiResponse = apiResponse.data;
                }

                return apiResponse.map((job: any) => ({
                    id: job.id,
                    name: `${job.job_requisition_key} - ${job.company.name} - ${job.title}`
                }));

            default:
                return [];
        }
    } catch (error) {
        console.log('JobAsOptionsList', error);
        return [];
    }
}
